/*
  <collection-filters> — filtering, sorting and pagination for main-collection-product-grid
  without page reloads. The section is re-fetched with ?section_id= and these regions are swapped:
  - [data-results-meta]  count + active filter chips
  - [data-results]       product grid + pagination
  - [data-filter-body]   each filter group's options (except the one being edited)
  - [data-sort-label]    current sort label
  Group open/closed and "show all" states live on elements that are never replaced.
*/
if (!customElements.get('collection-filters')) {
  const toNumber = (value, fallback) => (value === '' || value === undefined ? fallback : Number(value));

  class CollectionFilters extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('[data-filters-form]');
      if (!this.form) return;
      this.sectionId = this.dataset.sectionId;
      this.columnsKey = 'collection-columns';
      this.cache = new Map();

      // Listen on the component: sort radios live in the toolbar and join the form via form="".
      this.addEventListener('change', (event) => event.target.form === this.form && this.onChange(event));
      this.addEventListener('input', (event) => event.target.form === this.form && this.onInput(event));
      this.form.addEventListener('submit', (event) => {
        event.preventDefault();
        this.submit();
      });

      this.addEventListener('click', (event) => this.onClick(event));
      this.addEventListener('keyup', (event) => event.key === 'Escape' && this.closeOverlays());
      window.addEventListener('popstate', () => this.render(window.location.search.slice(1), null, false));

      this.restoreColumns();
      this.querySelectorAll('[data-price-range]').forEach((range) => this.syncPriceRange(range));
    }

    /* Events */

    onChange(event) {
      const { target } = event;
      if (target.matches('[data-price-slider]')) return;
      if (target.name === 'sort_by') this.querySelector('[data-sort]')?.removeAttribute('open');
      if (target.matches('[data-price-input]')) this.syncPriceRange(target.closest('[data-price-range]'));
      this.submit(target);
    }

    onInput(event) {
      const { target } = event;
      if (!target.matches('[data-price-slider]')) return;

      const range = target.closest('[data-price-range]');
      const [minSlider, maxSlider] = range.querySelectorAll('[data-price-slider]');
      const [minInput, maxInput] = range.querySelectorAll('[data-price-input]');

      if (Number(minSlider.value) > Number(maxSlider.value)) {
        target === minSlider ? (minSlider.value = maxSlider.value) : (maxSlider.value = minSlider.value);
      }

      minInput.value = Number(minSlider.value) > 0 ? minSlider.value : '';
      maxInput.value = Number(maxSlider.value) < Number(maxSlider.max) ? maxSlider.value : '';
      this.updateRangeFill(range);

      clearTimeout(this.sliderTimer);
      this.sliderTimer = setTimeout(() => this.submit(target), 500);
    }

    onClick(event) {
      const tier = event.target.closest('[data-price-tier]');
      if (tier) return this.applyTier(tier);

      const showMore = event.target.closest('[data-show-more]');
      if (showMore) {
        const group = showMore.closest('[data-filter-group]');
        const expanded = group.classList.toggle('is-expanded');
        showMore.setAttribute('aria-expanded', expanded);
        return;
      }

      const columns = event.target.closest('[data-columns]');
      if (columns) return this.setColumns(columns.dataset.columns, true);

      if (event.target.closest('[data-filters-open]')) return this.toggleDrawer(true);
      if (event.target.closest('[data-filters-close]')) return this.toggleDrawer(false);

      const link = event.target.closest('a[data-ajax-link]');
      if (link) {
        event.preventDefault();
        const scrollToResults = link.closest('[data-pagination]');
        this.render(new URL(link.href).searchParams.toString());
        if (scrollToResults) this.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }

    /* Rendering */

    submit(source = null) {
      const params = new URLSearchParams();
      for (const [key, value] of new FormData(this.form)) {
        if (value !== '') params.append(key, value);
      }
      this.render(params.toString(), source);
    }

    render(searchParams, source = null, updateHistory = true) {
      const url = `${window.location.pathname}?section_id=${this.sectionId}${searchParams ? `&${searchParams}` : ''}`;
      this.classList.add('is-loading');

      const request = this.cache.has(url)
        ? Promise.resolve(this.cache.get(url))
        : fetch(url).then((response) => response.text());

      request
        .then((html) => {
          this.cache.set(url, html);
          this.swap(new DOMParser().parseFromString(html, 'text/html'), source);
          if (updateHistory) {
            history.pushState({}, '', `${window.location.pathname}${searchParams ? `?${searchParams}` : ''}`);
          }
        })
        .catch((error) => console.error(error))
        .finally(() => this.classList.remove('is-loading'));
    }

    swap(html, source) {
      const replace = (selector) => {
        const next = html.querySelector(selector);
        const current = this.querySelector(selector);
        if (next && current) current.innerHTML = next.innerHTML;
      };

      replace('[data-results-meta]');
      replace('[data-results]');
      replace('[data-sort-label]');
      replace('[data-filters-count]');

      const activeGroup = source?.closest('[data-filter-group]');
      this.querySelectorAll('[data-filter-group]').forEach((group) => {
        if (group === activeGroup && source.matches('[data-price-input], [data-price-slider]')) return;
        const next = html.getElementById(group.id);
        const body = group.querySelector('[data-filter-body]');
        if (next && body) body.innerHTML = next.querySelector('[data-filter-body]').innerHTML;
      });

      this.querySelectorAll('[data-price-range]').forEach((range) => this.syncPriceRange(range));
    }

    /* Price range */

    syncPriceRange(range) {
      if (!range) return;
      const [minSlider, maxSlider] = range.querySelectorAll('[data-price-slider]');
      const [minInput, maxInput] = range.querySelectorAll('[data-price-input]');
      minSlider.value = minInput.value || 0;
      maxSlider.value = maxInput.value || maxSlider.max;
      this.updateRangeFill(range);
      this.updateTiers(range);
    }

    updateRangeFill(range) {
      const [minSlider, maxSlider] = range.querySelectorAll('[data-price-slider]');
      const max = Number(maxSlider.max) || 1;
      range.style.setProperty('--range-start', `${(Number(minSlider.value) / max) * 100}%`);
      range.style.setProperty('--range-end', `${(Number(maxSlider.value) / max) * 100}%`);
    }

    applyTier(tier) {
      const range = tier.closest('[data-filter-group]').querySelector('[data-price-range]');
      const [minInput, maxInput] = range.querySelectorAll('[data-price-input]');
      const isActive = tier.getAttribute('aria-pressed') === 'true';
      minInput.value = isActive ? '' : tier.dataset.min;
      maxInput.value = isActive ? '' : tier.dataset.max;
      this.syncPriceRange(range);
      this.submit(minInput);
    }

    updateTiers(range) {
      const [minInput, maxInput] = range.querySelectorAll('[data-price-input]');
      range
        .closest('[data-filter-group]')
        .querySelectorAll('[data-price-tier]')
        .forEach((tier) => {
          const hasValue = minInput.value !== '' || maxInput.value !== '';
          const active =
            hasValue &&
            toNumber(minInput.value, 0) === toNumber(tier.dataset.min, 0) &&
            toNumber(maxInput.value, Infinity) === toNumber(tier.dataset.max, Infinity);
          tier.setAttribute('aria-pressed', active ? 'true' : 'false');
        });
    }

    /* Columns, drawer */

    restoreColumns() {
      let stored = null;
      try {
        stored = window.localStorage.getItem(this.columnsKey);
      } catch (error) {}
      this.setColumns(stored || this.dataset.columns, false);
    }

    setColumns(columns, persist) {
      this.style.setProperty('--columns-desktop', columns);
      this.querySelectorAll('[data-columns]').forEach((button) => {
        button.setAttribute('aria-pressed', button.dataset.columns === String(columns));
      });
      if (!persist) return;
      try {
        window.localStorage.setItem(this.columnsKey, columns);
      } catch (error) {}
    }

    toggleDrawer(open) {
      this.classList.toggle('is-drawer-open', open);
      document.body.classList.toggle('overflow-hidden', open);
      if (open) this.querySelector('[data-filters-close]')?.focus();
    }

    closeOverlays() {
      this.querySelector('[data-sort]')?.removeAttribute('open');
      if (this.classList.contains('is-drawer-open')) this.toggleDrawer(false);
    }
  }

  customElements.define('collection-filters', CollectionFilters);
}
